import { Fragment, useEffect, useRef, useState } from "react";
import AnchoredPanel from "../../../../GlobalComponents/AnchoredPanel/AnchoredPanel";
import FactorBreakdownFields from "./FactorBreakdownFields";
import { hasGasBreakdown } from "../../model/helpers";

let listIdCounter = 0;

/**
 * Editor for a KPI's GHG factors, anchored under the cell.
 *
 * A factor is an address plus a number: scope, country, unit and source
 * database say what is being priced and by whose figure, and the value prices
 * it. All four parts are required, which is why each factor gets a row of
 * controls rather than a single input.
 *
 * Several factors per KPI is normal, not an edge case — KPI 6697 carries three
 * across two databases — so the rows stack, in the order the endpoint returns
 * them (database priority).
 *
 * A factor built gas by gas (CO2, CH4, N2O with density, NCV and GWP) opens
 * its breakdown under the row; its value is then calculated, not typed.
 *
 * @param {object} props
 * @param {import("../../model/helpers").GridFactor[]} props.factors
 * @param {{ value: string, label: string }[]} props.scopeOptions
 * @param {string[]} props.countrySuggestions
 * @param {{ id: number, unit: string, unit_name: string }[]} props.unitMaster
 * @param {{ id: number, name: string }[]} props.databases
 * @param {string} [props.error]
 * @param {(index: number, patch: object) => void} props.onChange
 * @param {(index: number) => void} props.onRemove
 * @param {() => void} props.onAdd
 * @param {() => void} props.onClose
 */
export default function EmissionFactorsEditor({
  anchorRef,
  factors,
  scopeOptions,
  countrySuggestions,
  unitMaster,
  databases,
  error,
  onChange,
  onRemove,
  onAdd,
  onClose,
}) {
  const listIdRef = useRef(null);
  if (listIdRef.current === null) listIdRef.current = ++listIdCounter;

  const lastValueRef = useRef(null);
  const shownCount = useRef(0);
  useEffect(() => {
    if (factors.length > shownCount.current && lastValueRef.current) lastValueRef.current.focus();
    shownCount.current = factors.length;
  }, [factors.length]);

  const onValueKeyDown = (e, index) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (index === factors.length - 1) onAdd();
  };

  const [openBreakdowns, setOpenBreakdowns] = useState(() => new Set());
  const toggleBreakdown = (index) =>
    setOpenBreakdowns((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const removeFactor = (index) => {
    setOpenBreakdowns(
      (current) =>
        new Set([...current].filter((open) => open !== index).map((open) => (open > index ? open - 1 : open))),
    );
    onRemove(index);
  };

  const unitName = (unitId) => unitMaster.find((unit) => Number(unit.id) === Number(unitId))?.unit || "unit";

  const countryListId = `kgb-countries-${listIdRef.current}`;

  return (
    <AnchoredPanel anchorRef={anchorRef} onClose={onClose} className="kgb-picker kgb-picker--widest">
      <p className="kgb-picker-title">Emission factors</p>

      {factors.length === 0 ? (
        <p className="kgb-picker-empty">No factors yet</p>
      ) : (
        <div className="kgb-factorrow kgb-factorhead" aria-hidden="true">
          <span className="kgb-factor-scope">Scope</span>
          <span className="kgb-factor-country">Country</span>
          <span className="kgb-factor-unit">Per unit</span>
          <span className="kgb-factor-value">Value</span>
          <span className="kgb-factor-db">Source</span>
          <span className="kgb-factor-gas">Gases</span>
          <span className="kgb-editor-remove" />
        </div>
      )}

      {factors.map((factor, index) => {
        const gasWise = hasGasBreakdown(factor);
        const breakdownOpen = openBreakdowns.has(index);
        return (
          <Fragment key={factor.id ?? `new-${index}`}>
            <div className="kgb-factorrow">
              <select
                className="kgb-factor-scope"
                value={factor.scope}
                aria-label="Scope"
                onChange={(e) => onChange(index, { scope: e.target.value })}
              >
                {scopeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <input
                type="text"
                className="kgb-factor-country"
                value={factor.countryCode}
                list={countryListId}
                placeholder="IN"
                aria-label="Country code"
                onChange={(e) => onChange(index, { countryCode: e.target.value.toUpperCase() })}
              />

              <select
                className="kgb-factor-unit"
                value={factor.unitId}
                aria-label="Unit the factor is quoted per"
                onChange={(e) => onChange(index, { unitId: e.target.value ? Number(e.target.value) : "" })}
              >
                <option value="">per unit…</option>
                {unitMaster.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.unit}
                  </option>
                ))}
              </select>

              <input
                type="number"
                className="kgb-factor-value"
                value={factor.value}
                min="0"
                step="any"
                placeholder="0.0"
                aria-label="Emission factor"
                readOnly={gasWise}
                title={gasWise ? "Calculated from the gas factors" : undefined}
                ref={index === factors.length - 1 ? lastValueRef : null}
                onKeyDown={(e) => onValueKeyDown(e, index)}
                onChange={(e) => onChange(index, { value: e.target.value })}
              />

              <select
                className="kgb-factor-db"
                value={factor.databaseId}
                aria-label="Source database"
                onChange={(e) => onChange(index, { databaseId: e.target.value })}
              >
                <option value="">source…</option>
                {databases.map((database) => (
                  <option key={database.id} value={String(database.id)}>
                    {database.name}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className={`kgb-factor-gas${gasWise ? " kgb-factor-gas--set" : ""}`}
                aria-expanded={breakdownOpen}
                onClick={() => toggleBreakdown(index)}
              >
                {gasWise ? "Gas-wise ✓" : "Gas-wise"}
              </button>

              <button
                type="button"
                className="kgb-editor-remove"
                aria-label={`Remove factor ${index + 1}`}
                onClick={() => removeFactor(index)}
              >
                ×
              </button>
            </div>
            {breakdownOpen ? (
              <FactorBreakdownFields
                factor={factor}
                unitName={unitName(factor.unitId)}
                onChange={(patch) => onChange(index, patch)}
              />
            ) : null}
          </Fragment>
        );
      })}

      <datalist id={countryListId}>
        {countrySuggestions.map((code) => (
          <option key={code} value={code} />
        ))}
      </datalist>

      {error ? <p className="kgb-editor-error">{error}</p> : null}

      <div className="kgb-editor-actions">
        <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onAdd}>
          + Add factor
        </button>
        <span className="kgb-editor-hint">Enter adds the next</span>
      </div>
    </AnchoredPanel>
  );
}
